import StyleConstants from '../../../core/styles.constants';
import type { Coordinate, MultiPolygonCoordinates } from '../models/editor-feature';
import type { Draft } from '../hooks/use-drawing';
import type { DrawCommand, DrawLayerTag } from './draw-plan';
import { shapeBetween } from './drawing';
import { MDI_VIEWBOX_SIZE } from './mdi-icons';
import { toScreen, type Projection } from './projection';
import {
  handlesOf,
  HANDLE_SIZE,
  ROTATE_HANDLE_RADIUS,
  type SelectionBounds,
} from './selection-chrome';
import type { ViewBox } from '../hooks/use-viewport';
import { editorTheme, layerPalette } from './editor-theme';

/**
 * Pushes a draw plan into a 2D context.
 *
 * Deliberately the only part of the renderer that knows a canvas exists, and deliberately
 * the only part with no tests of its own: every decision worth asserting — what is drawn, in
 * what order, in what colour — was made in `draw-plan`, and what is left here is the
 * mechanical translation. Testing it would mean asserting against a mock context, which pins
 * the implementation and not one thing a user could notice.
 */

/** Where the surface sits inside the element, honouring the aspect ratio rather than stretching. */
export interface SurfaceTransform {
  /** Element pixels per surface unit. */
  readonly scale: number;
  /** Element-space offset of the surface origin, from letterboxing. */
  readonly originX: number;
  readonly originY: number;
}

/**
 * Fits the viewBox into the element the way SVG's `xMidYMid meet` does: scale to fit the
 * tighter axis, and centre the slack on the other.
 *
 * Extracted because two places need it and used to each do their own version — and the
 * pointer maths only agreed with the drawing while the element happened to share the
 * surface's aspect ratio. Off a 4:3 surface it did not, and a click landed a little further
 * from the cursor the further the pointer got from the centre.
 */
export const surfaceTransformOf = (
  elementWidth: number,
  elementHeight: number,
  viewBox: ViewBox,
): SurfaceTransform => {
  if (viewBox.width <= 0 || viewBox.height <= 0) {
    return { scale: 1, originX: 0, originY: 0 };
  }

  const scale = Math.min(elementWidth / viewBox.width, elementHeight / viewBox.height);

  return {
    scale,
    originX: (elementWidth - viewBox.width * scale) / 2 - viewBox.x * scale,
    originY: (elementHeight - viewBox.height * scale) / 2 - viewBox.y * scale,
  };
};

/** Where in the surface's own coordinates a point on the element falls. */
export const elementToSurface = (
  point: { x: number; y: number },
  transform: SurfaceTransform,
): { x: number; y: number } => ({
  x: (point.x - transform.originX) / transform.scale,
  y: (point.y - transform.originY) / transform.scale,
});

const traceMultiPolygon = (
  context: CanvasRenderingContext2D,
  coordinates: MultiPolygonCoordinates,
  projection: Projection,
): void => {
  context.beginPath();

  coordinates.forEach((polygon) => {
    polygon.forEach((ring) => {
      ring.forEach((vertex, index) => {
        const { x, y } = toScreen(vertex, projection);

        if (index === 0) {
          context.moveTo(x, y);
        } else {
          context.lineTo(x, y);
        }
      });

      context.closePath();
    });
  });
};

const withRotation = (
  context: CanvasRenderingContext2D,
  at: Coordinate,
  rotationDegrees: number,
  projection: Projection,
  body: () => void,
): void => {
  const { x, y } = toScreen(at, projection);

  context.save();
  context.translate(x, y);
  // Positive means clockwise as the admin sees it, on a Y-down surface — the same convention
  // the whole editor uses, and the one a desk shape's real vertex rotation is negated into.
  context.rotate((rotationDegrees * Math.PI) / 180);
  body();
  context.restore();
};

const paintCommand = (
  context: CanvasRenderingContext2D,
  command: DrawCommand,
  projection: Projection,
): void => {
  switch (command.kind) {
    case 'area': {
      traceMultiPolygon(context, command.coordinates, projection);

      if (command.fill) {
        // Multiplied into whatever alpha the layer is already drawn at, and put back after.
        // Setting it outright — and resetting it to 1 — threw away the fade an editing mode puts
        // on the layers it is not working on, so nothing was ever actually dimmed.
        const layerAlpha = context.globalAlpha;

        context.globalAlpha = layerAlpha * command.fillOpacity;
        context.fillStyle = command.fill;
        // `evenodd`, so a ring drawn inside another reads as a hole rather than painting over
        // it — which is what a room with a core in it, or a wall with an opening, actually is.
        context.fill('evenodd');
        context.globalAlpha = layerAlpha;
      }

      if (command.stroke) {
        context.strokeStyle = command.stroke;
        context.lineWidth = command.strokeWidth;
        context.stroke();
      }

      break;
    }

    case 'polyline': {
      context.beginPath();

      command.paths.forEach((path) => {
        path.forEach((vertex, index) => {
          const { x, y } = toScreen(vertex, projection);

          if (index === 0) {
            context.moveTo(x, y);
          } else {
            context.lineTo(x, y);
          }
        });
      });

      context.strokeStyle = command.stroke;
      context.lineWidth = command.strokeWidth;
      context.stroke();
      break;
    }

    case 'desk': {
      withRotation(context, command.at, command.rotation, projection, () => {
        if (command.iconPath) {
          // Drawn in the desk's own colour rather than a colour of its own, so the icon still
          // says the one thing the marker has always said: bookable, not bookable, or out of
          // service. A green printer and an amber one are two states of the same desk.
          // Sized by the draw plan, which is where the desk's real dimensions are known.
          const size = command.iconSize;
          const scale = size / MDI_VIEWBOX_SIZE;

          context.save();
          context.scale(scale, scale);
          context.translate(-MDI_VIEWBOX_SIZE / 2, -MDI_VIEWBOX_SIZE / 2);

          const glyph = new Path2D(command.iconPath);
          context.fillStyle = command.dashed ? StyleConstants.GREY_COLOR : command.fill;
          context.fill(glyph);

          // The outline carries selection and hover, exactly as the marker's does.
          context.strokeStyle = command.stroke;
          context.lineWidth = (command.strokeWidth * MDI_VIEWBOX_SIZE) / size;
          context.stroke(glyph);

          context.restore();
          return;
        }

        context.beginPath();
        context.arc(0, 0, command.radius, 0, Math.PI * 2);
        context.fillStyle = command.fill;
        context.fill();

        context.setLineDash(command.dashed ? [2, 2] : []);
        context.strokeStyle = command.stroke;
        context.lineWidth = command.strokeWidth;
        context.stroke();
        context.setLineDash([]);

        // The notch is what makes rotation visible: a desk's geometry is a bare point, so
        // without it a turned desk looks identical to an unturned one and the admin has no
        // feedback that the edit landed.
        context.beginPath();
        context.moveTo(0, 0);
        context.lineTo(0, -(command.radius + 3));
        context.strokeStyle = StyleConstants.GREY_COLOR;
        context.lineWidth = 1;
        context.stroke();

        if (command.pod) {
          context.beginPath();
          context.arc(0, 0, command.radius + 3, 0, Math.PI * 2);
          context.setLineDash([2, 2]);
          context.strokeStyle = StyleConstants.DARK_BLUE_COLOR;
          context.lineWidth = 0.75;
          context.stroke();
          context.setLineDash([]);
        }
      });

      break;
    }

    case 'text': {
      withRotation(context, command.at, command.rotation, projection, () => {
        context.font = `${command.fontSize}px ${StyleConstants.HEADER_FONT_FAMILY}`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillStyle = command.color;
        context.fillText(command.text, 0, 0);

        if (command.outline) {
          context.strokeStyle = command.outline;
          context.lineWidth = command.outlineWidth;
          context.strokeText(command.text, 0, 0);
        }
      });

      break;
    }

    case 'icon': {
      withRotation(context, command.at, command.rotation, projection, () => {
        if (command.path) {
          // Scaled from MDI's own 24×24 canvas to the size stored on the annotation, and
          // shifted so the glyph centres on the anchor rather than hanging off its corner.
          const scale = command.size / MDI_VIEWBOX_SIZE;

          context.save();
          context.scale(scale, scale);
          context.translate(-MDI_VIEWBOX_SIZE / 2, -MDI_VIEWBOX_SIZE / 2);

          const glyph = new Path2D(command.path);
          context.fillStyle = command.color;
          context.fill(glyph);

          if (command.outlined) {
            context.strokeStyle = layerPalette.selection;
            context.lineWidth = MDI_VIEWBOX_SIZE / command.size;
            context.stroke(glyph);
          }

          context.restore();
          return;
        }

        if (!command.placeholder) {
          return;
        }

        context.setLineDash([3, 2]);
        context.strokeStyle = StyleConstants.PINK_COLOR;
        context.lineWidth = 1;
        context.strokeRect(-command.size / 2, -command.size / 2, command.size, command.size);
        context.setLineDash([]);

        context.font = `${command.size * 0.6}px ${StyleConstants.HEADER_FONT_FAMILY}`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillStyle = StyleConstants.PINK_COLOR;
        context.fillText('?', 0, 0);
      });

      break;
    }

    default:
      break;
  }
};

export interface PaintOptions {
  readonly commands: readonly DrawCommand[];
  readonly projection: Projection;
  readonly viewBox: ViewBox;
  /** CSS pixels, not backing-store pixels. */
  readonly width: number;
  readonly height: number;
  readonly devicePixelRatio: number;
  /** Surface-space rectangle of a marquee in progress, if there is one. */
  readonly marquee?: { x: number; y: number; width: number; height: number };
  /** The selection box, when something is selected. */
  readonly selectionBounds?: SelectionBounds;
  readonly selectionResizable?: boolean;
  /** Shown mid-gesture so a resize or a turn has a number, not just a feel. */
  readonly readout?: string;
  /** The shape currently being drawn, before it becomes a feature. */
  readonly draft?: Draft;
  /** What the draft measures so far, printed beside the pointer. */
  readonly draftReadout?: string;
  /** Whether the static background ruling is drawn behind the plan. */
  readonly showGrid?: boolean;
  /**
   * The points of the shapes being reshaped, in surface units.
   *
   * Drawn only while the vertex tool is armed. Every corner of every shape on a plan is
   * thousands of dots, which is not a drawing anybody can work on — so it is the selection's
   * points or none.
   */
  readonly vertices?: readonly { x: number; y: number }[];
  /**
   * The middle of each side of the shapes being reshaped, in surface units.
   *
   * Drawn as round grips, apart from the square corner ones, because they do something
   * different: pressing one adds a corner there, or — when sides are being pushed and pulled —
   * takes hold of the whole side.
   */
  readonly edgeHandles?: readonly { x: number; y: number }[];
  /**
   * Layers drawn faded, so the one being edited stands out.
   *
   * What makes "Edit rooms" read as a mode: every room stays at full strength and everything
   * else steps back, so the admin can see at a glance which shapes a click will reach.
   */
  readonly dimmedLayers?: ReadonlySet<DrawLayerTag>;
  /**
   * The snapping grid's spacing in plan units, when snapping to the grid is on.
   *
   * Drawn as dots that move with the plan — unlike the background ruling, which is fixed to the
   * screen and is not what anything snaps to. Showing where points will land is the difference
   * between a grid that helps and a pointer that seems to jump about.
   */
  readonly snapGridSpacing?: number;
  /** Where the pointer was just snapped to, in surface units, and to what. */
  readonly snapMarker?: { readonly x: number; readonly y: number; readonly kind: 'point' | 'grid' | 'none' };
  /**
   * Alignment guides while something is being lined up, in surface units: a line through
   * everything that now sits level or upright with what is being moved.
   */
  readonly guides?: readonly { readonly from: { x: number; y: number }; readonly to: { x: number; y: number } }[];
}

/** The gap between grid lines, in CSS pixels. Every fifth is drawn heavier. */
const GRID_PITCH_PIXELS = 24;
const GRID_MAJOR_EVERY = 5;

/**
 * The background ruling, drawn in screen space rather than on the plan.
 *
 * Deliberately *not* zoomed or panned with the drawing. A grid that tracks the plan is a
 * second thing moving under the eye during every zoom, and at a far enough zoom it is either
 * a grey wash or two lines on the whole screen. Fixed to the viewport it behaves like the
 * sheet of graph paper the plan is laid on: it gives the eye something still to judge the
 * drawing's movement against, and it costs nothing at any zoom.
 *
 * It is therefore a backdrop and not a snapping guide — snapping rounds to a spacing in the
 * plan's own units, which is a different grid and one that would have to move with the plan.
 */
const paintStaticGrid = (
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
): void => {
  context.save();

  const lines = (step: number, colour: string, onlyMajor: boolean): void => {
    context.strokeStyle = colour;
    context.lineWidth = 1;
    context.beginPath();

    for (let x = 0, index = 0; x <= width; x += step, index += 1) {
      if ((index % GRID_MAJOR_EVERY === 0) === onlyMajor) {
        // Half a pixel, so a one-pixel line lands on a pixel rather than straddling two and
        // rendering as two grey ones.
        context.moveTo(Math.round(x) + 0.5, 0);
        context.lineTo(Math.round(x) + 0.5, height);
      }
    }

    for (let y = 0, index = 0; y <= height; y += step, index += 1) {
      if ((index % GRID_MAJOR_EVERY === 0) === onlyMajor) {
        context.moveTo(0, Math.round(y) + 0.5);
        context.lineTo(width, Math.round(y) + 0.5);
      }
    }

    context.stroke();
  };

  lines(GRID_PITCH_PIXELS, layerPalette.gridMinor, false);
  lines(GRID_PITCH_PIXELS, layerPalette.gridMajor, true);
  context.restore();
};

/** A rounded rectangle path. `roundRect` is not in every browser this ships to. */
const roundedRect = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void => {
  const limit = Math.min(radius, width / 2, height / 2);

  context.beginPath();
  context.moveTo(x + limit, y);
  context.arcTo(x + width, y, x + width, y + height, limit);
  context.arcTo(x + width, y + height, x, y + height, limit);
  context.arcTo(x, y + height, x, y, limit);
  context.arcTo(x, y, x + width, y, limit);
  context.closePath();
};

/**
 * A number printed over the drawing: a running measurement, a resize percentage, an angle.
 *
 * On a pill rather than as bare text. These land wherever the pointer is, which is over the
 * drawing by definition, and dark text on a dark wall is unreadable exactly when it is being
 * read — mid-gesture, with no way to move the thing underneath it.
 */
const paintPill = (
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  inverse: number,
): void => {
  const fontSize = 12 * inverse;
  const paddingX = 7 * inverse;
  const paddingY = 4 * inverse;

  context.save();
  context.font = `600 ${fontSize}px ${StyleConstants.HEADER_FONT_FAMILY}`;
  context.textAlign = 'center';
  context.textBaseline = 'middle';

  const width = context.measureText(text).width + paddingX * 2;
  const height = fontSize + paddingY * 2;

  context.fillStyle = layerPalette.selection;
  roundedRect(context, x - width / 2, y - height / 2, width, height, 4 * inverse);
  context.fill();

  context.fillStyle = editorTheme.accentInk;
  context.fillText(text, x, y);
  context.restore();
};

/**
 * The shape being drawn, and the segment being aimed.
 *
 * Drawn in the selection blue rather than the colour the finished feature will be: a draft is
 * not yet part of the plan, and showing it as though it were makes an abandoned one look like
 * a shape that failed to delete.
 */
const paintDraft = (
  context: CanvasRenderingContext2D,
  draft: Draft,
  readout: string | undefined,
  projection: Projection,
  inverse: number,
): void => {
  const points = draft.cursor ? [...draft.points, draft.cursor] : [...draft.points];

  if (!points.length) {
    return;
  }

  context.save();
  context.strokeStyle = layerPalette.draft;
  context.lineWidth = 1.5 * inverse;

  const cornered = draft.kind !== 'polygon';

  if (cornered && points.length >= 2) {
    const from = toScreen(points[0], projection);
    const to = toScreen(points[points.length - 1], projection);

    context.setLineDash([4 * inverse, 3 * inverse]);

    if (draft.kind === 'line') {
      context.beginPath();
      context.moveTo(from.x, from.y);
      context.lineTo(to.x, to.y);
      context.stroke();
    } else if (draft.kind === 'square' || draft.kind === 'circle' || draft.kind === 'triangle') {
      // Previewed from the very geometry the click will commit, so a square that snaps to the
      // longer side looks, while aiming, exactly like the square that lands.
      const shape = shapeBetween(draft.kind, points[0], points[points.length - 1]);

      shape?.forEach((polygon) => polygon.forEach((ring) => {
        context.beginPath();
        ring.forEach((vertex, index) => {
          const { x, y } = toScreen(vertex, projection);

          if (index === 0) {
            context.moveTo(x, y);
          } else {
            context.lineTo(x, y);
          }
        });
        context.stroke();
      }));
    } else if (draft.kind === 'ellipse') {
      // Drawn as the real curve while it is only a preview. What lands on the plan is a polygon
      // of forty-eight edges, and at the size these are drawn the two are the same picture.
      context.beginPath();
      context.ellipse(
        (from.x + to.x) / 2,
        (from.y + to.y) / 2,
        Math.abs(to.x - from.x) / 2,
        Math.abs(to.y - from.y) / 2,
        0,
        0,
        Math.PI * 2,
      );
      context.stroke();
    } else {
      context.strokeRect(from.x, from.y, to.x - from.x, to.y - from.y);
    }

    context.setLineDash([]);
  } else {
    context.beginPath();
    points.forEach((point, index) => {
      const { x, y } = toScreen(point, projection);

      if (index === 0) {
        context.moveTo(x, y);
      } else {
        context.lineTo(x, y);
      }
    });

    // The closing segment is dashed, so what is committed is told apart from what closing the
    // shape would add.
    if (draft.kind === 'polygon' && points.length >= 3) {
      context.stroke();
      context.setLineDash([4 * inverse, 3 * inverse]);
      context.beginPath();
      const last = toScreen(points[points.length - 1], projection);
      const first = toScreen(points[0], projection);
      context.moveTo(last.x, last.y);
      context.lineTo(first.x, first.y);
      context.stroke();
      context.setLineDash([]);
    } else {
      context.stroke();
    }
  }

  // A dot per committed vertex, so it is obvious which points are placed and which is the
  // one still following the pointer.
  draft.points.forEach((point) => {
    const { x, y } = toScreen(point, projection);
    context.beginPath();
    context.arc(x, y, 3.5 * inverse, 0, Math.PI * 2);
    context.fillStyle = editorTheme.surface;
    context.fill();
    context.stroke();
  });

  if (readout && draft.cursor) {
    const { x, y } = toScreen(draft.cursor, projection);

    // Beside the pointer rather than under it, and on the same pill the other running
    // numbers use, so a length while drawing and a percentage while resizing look like the
    // same kind of thing.
    paintPill(context, readout, x + 34 * inverse, y - 18 * inverse, inverse);
  }

  context.restore();
};

/**
 * The points of a shape being reshaped.
 *
 * Square and white with an accent border, like the resize grips — they are the same kind of
 * thing to the hand, and a different shape for each would say they behave differently.
 */
const paintVertices = (
  context: CanvasRenderingContext2D,
  vertices: readonly { x: number; y: number }[],
  inverse: number,
): void => {
  const size = 7 * inverse;

  context.save();
  context.fillStyle = editorTheme.surface;
  context.strokeStyle = layerPalette.selection;
  context.lineWidth = 1.25 * inverse;

  vertices.forEach(({ x, y }) => {
    context.fillRect(x - size / 2, y - size / 2, size, size);
    context.strokeRect(x - size / 2, y - size / 2, size, size);
  });

  context.restore();
};

/** The fewest screen pixels between snapping grid dots before they are too dense to draw. */
const SNAP_GRID_MIN_PIXELS = 8;

/** Past this many dots the grid is left undrawn rather than stalling the frame. */
const SNAP_GRID_MAX_DOTS = 40000;

const paintSnapGrid = (
  context: CanvasRenderingContext2D,
  spacing: number,
  projection: Projection,
  viewBox: ViewBox,
  zoomScale: number,
  inverse: number,
): void => {
  if (!(spacing > 0) || spacing * projection.scale * zoomScale < SNAP_GRID_MIN_PIXELS) {
    return;
  }

  // The plan-space extent of what is on screen, from the surface-space view box.
  const toPlanX = (x: number): number => (x - projection.offsetX) / projection.scale + projection.boundingBox.minX;
  const toPlanY = (y: number): number => (
    (projection.height - y - projection.offsetY) / projection.scale + projection.boundingBox.minY
  );

  const fromX = Math.floor(toPlanX(viewBox.x) / spacing);
  const toX = Math.ceil(toPlanX(viewBox.x + viewBox.width) / spacing);
  const fromY = Math.floor(toPlanY(viewBox.y + viewBox.height) / spacing);
  const toY = Math.ceil(toPlanY(viewBox.y) / spacing);

  if ((toX - fromX + 1) * (toY - fromY + 1) > SNAP_GRID_MAX_DOTS) {
    return;
  }

  const size = 2 * inverse;

  context.save();
  context.fillStyle = layerPalette.gridMajor;

  for (let column = fromX; column <= toX; column += 1) {
    for (let row = fromY; row <= toY; row += 1) {
      const { x, y } = toScreen([column * spacing, row * spacing], projection);
      context.fillRect(x - size / 2, y - size / 2, size, size);
    }
  }

  context.restore();
};

/** A ring where the pointer was pulled to: filled for a shape's corner, hollow for the grid. */
const paintSnapMarker = (
  context: CanvasRenderingContext2D,
  marker: { x: number; y: number; kind: 'point' | 'grid' | 'none' },
  inverse: number,
): void => {
  context.save();
  context.lineWidth = 2 * inverse;
  context.strokeStyle = layerPalette.selection;
  context.fillStyle = marker.kind === 'point' ? layerPalette.selectionFill : editorTheme.surface;
  context.beginPath();
  context.arc(marker.x, marker.y, 7 * inverse, 0, Math.PI * 2);
  context.fill();
  context.stroke();
  context.restore();
};

/**
 * The colour of an alignment guide: the one hue on the surface that is none of the layers'
 * and not the selection's, so a guide can never be mistaken for part of the drawing.
 */
const GUIDE_COLOUR = '#E8388A';

const paintGuides = (
  context: CanvasRenderingContext2D,
  guides: readonly { from: { x: number; y: number }; to: { x: number; y: number } }[],
  inverse: number,
): void => {
  // Carried a little past both ends, so a guide between two corners reads as a line and not as
  // an edge of either shape.
  const overhang = 12 * inverse;

  context.save();
  context.strokeStyle = GUIDE_COLOUR;
  context.lineWidth = inverse;
  context.setLineDash([5 * inverse, 3 * inverse]);

  guides.forEach(({ from, to }) => {
    const length = Math.hypot(to.x - from.x, to.y - from.y) || 1;
    const ux = (to.x - from.x) / length;
    const uy = (to.y - from.y) / length;

    context.beginPath();
    context.moveTo(from.x - (ux * overhang), from.y - (uy * overhang));
    context.lineTo(to.x + (ux * overhang), to.y + (uy * overhang));
    context.stroke();
  });

  context.restore();
};

/** How strongly a layer that is not being edited is drawn. Faint, but still there to aim by. */
const DIMMED_LAYER_ALPHA = 0.28;

/** Round grips at the middle of each side. See `PaintOptions.edgeHandles`. */
const paintEdgeHandles = (
  context: CanvasRenderingContext2D,
  handles: readonly { x: number; y: number }[],
  inverse: number,
): void => {
  const radius = 4.5 * inverse;

  context.save();
  context.fillStyle = layerPalette.selection;
  context.strokeStyle = editorTheme.surface;
  context.lineWidth = 1.5 * inverse;

  handles.forEach(({ x, y }) => {
    context.beginPath();
    context.arc(x, y, radius, 0, Math.PI * 2);
    context.fill();
    context.stroke();
  });

  context.restore();
};

/**
 * The selection box and its grips.
 *
 * Drawn at a constant on-screen size by dividing every length by the zoom: a handle that
 * scaled with the plan would be a speck at one zoom and cover the shape at another, and the
 * hit-testing it has to agree with works in surface units regardless.
 */
const paintSelectionChrome = (
  context: CanvasRenderingContext2D,
  bounds: SelectionBounds,
  resizable: boolean,
  readout: string | undefined,
  inverse: number,
): void => {
  context.save();

  // A solid box rather than a dashed one, and a wash inside it. Dashes read as "being
  // drawn"; this is a selection that already exists, and the draft has the dashes.
  context.fillStyle = layerPalette.selectionFill;
  context.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);

  context.strokeStyle = layerPalette.selection;
  context.lineWidth = 1.25 * inverse;
  context.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);

  handlesOf(bounds, resizable).forEach((handle) => {
    if (handle.kind === 'rotate') {
      // The stalk is what makes the grip read as attached to the box rather than as a stray
      // dot floating above it.
      context.beginPath();
      context.moveTo(handle.x, bounds.y);
      context.lineTo(handle.x, handle.y);
      context.stroke();

      context.beginPath();
      context.arc(handle.x, handle.y, ROTATE_HANDLE_RADIUS, 0, Math.PI * 2);
      context.fillStyle = editorTheme.surface;
      context.fill();
      context.stroke();
      return;
    }

    // White fill, accent border, slightly rounded: a grip has to read as something to take
    // hold of, and a flat square in the selection colour reads as part of the drawing.
    const size = HANDLE_SIZE;
    const left = handle.x - size / 2;
    const top = handle.y - size / 2;

    context.fillStyle = editorTheme.surface;
    context.fillRect(left, top, size, size);
    context.lineWidth = 1.25 * inverse;
    context.strokeRect(left, top, size, size);
  });

  if (readout) {
    paintPill(
      context,
      readout,
      bounds.x + bounds.width / 2,
      bounds.y + bounds.height + 16 * inverse,
      inverse,
    );
  }

  context.restore();
};

export const paint = (context: CanvasRenderingContext2D, options: PaintOptions): void => {
  const { width, height, devicePixelRatio: ratio } = options;

  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.clearRect(0, 0, width, height);

  // The workspace is deliberately darker than the drawing. A plan on a white page inside a
  // white panel has no edge, so it reads as "the screen" rather than as a sheet of paper
  // somebody is working on — and there is then nothing to say where the building stops.
  // White edge to edge: the whole canvas is the working surface, and the ruling over it is
  // what gives the eye something to judge the drawing against.
  context.fillStyle = editorTheme.workspace;
  context.fillRect(0, 0, width, height);

  const transform = surfaceTransformOf(width, height, options.viewBox);

  context.setTransform(
    transform.scale * ratio,
    0,
    0,
    transform.scale * ratio,
    transform.originX * ratio,
    transform.originY * ratio,
  );

  // Line widths are authored in surface units, so a stroke would thin out as the plan is
  // zoomed in and thicken as it is zoomed out. Scaling them back keeps a hairline a hairline.
  const inverse = 1 / transform.scale;
  context.lineJoin = 'round';

  // The ruling is painted through the screen transform rather than the world one — which is
  // the whole point of it. Restoring the world transform afterwards rather than saving and
  // restoring the context keeps the line settings above intact.
  //
  // There is no longer a drawn sheet under it. The workspace is white edge to edge, so paper
  // the same colour as the surface it sits on is a rectangle that says nothing; what marks
  // where the building ends is its own border layer, which is the honest answer — a plan with
  // no border reads as having none rather than borrowing one from the viewport's extent.
  if (options.showGrid) {
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    paintStaticGrid(context, width, height);
    context.setTransform(
      transform.scale * ratio,
      0,
      0,
      transform.scale * ratio,
      transform.originX * ratio,
      transform.originY * ratio,
    );
  }

  options.commands.forEach((command) => {
    context.save();

    if (options.dimmedLayers?.has(command.layer)) {
      context.globalAlpha = DIMMED_LAYER_ALPHA;
    }

    paintCommand(context, command, options.projection);
    context.restore();
  });

  // Over the drawing rather than under it: the floor plate is opaque, and dots beneath it would
  // vanish exactly where the building is — which is where anything is being snapped.
  if (options.snapGridSpacing) {
    paintSnapGrid(context, options.snapGridSpacing, options.projection, options.viewBox, transform.scale, inverse);
  }

  if (options.draft) {
    paintDraft(context, options.draft, options.draftReadout, options.projection, inverse);
  }

  if (options.edgeHandles?.length) {
    paintEdgeHandles(context, options.edgeHandles, inverse);
  }

  if (options.vertices?.length) {
    paintVertices(context, options.vertices, inverse);
  }

  if (options.selectionBounds) {
    paintSelectionChrome(
      context,
      options.selectionBounds,
      options.selectionResizable ?? true,
      options.readout,
      inverse,
    );
  }

  if (options.guides?.length) {
    paintGuides(context, options.guides, inverse);
  }

  if (options.snapMarker) {
    paintSnapMarker(context, options.snapMarker, inverse);
  }

  if (options.marquee) {
    context.save();
    context.fillStyle = layerPalette.selectionFill;
    context.fillRect(options.marquee.x, options.marquee.y, options.marquee.width, options.marquee.height);
    context.setLineDash([4 * inverse, 3 * inverse]);
    context.strokeStyle = layerPalette.selection;
    context.lineWidth = inverse;
    context.strokeRect(options.marquee.x, options.marquee.y, options.marquee.width, options.marquee.height);
    context.restore();
  }
};

export default paint;
